import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";

// Concrete proof points instead of percentage bars. Every number here is
// backed by data in src/constant/data.js or a live store listing.
const proofPoints = [
  {
    k: "30",
    label: "five-star reviews",
    body: "Every Fiverr review to date is 5 stars, from one-day fixes to multi-month builds.",
  },
  {
    k: "9",
    label: "countries",
    body: "Clients in the US, Canada, Germany, Italy, South Africa, Pakistan and more.",
  },
  {
    k: "$30K+",
    label: "client revenue",
    body: "Generated by OraLive, a live-streaming app we built, designed and shipped.",
  },
];

function ProofPoint({ k, label, body, i }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.7, delay: 0.1 * i, ease: [0.22, 1, 0.36, 1] }}
      className="glass rounded-xl p-4 min-w-0"
    >
      <div className="font-jamjuree text-3xl md:text-4xl font-semibold leading-none text-gradient-aurora">
        {k}
      </div>
      <div className="mt-2 text-[11px] uppercase tracking-[0.16em] text-white/60 whitespace-nowrap">
        {label}
      </div>
      <p className="mt-3 text-xs text-white/55 leading-relaxed">{body}</p>
    </motion.div>
  );
}

const Section2 = () => {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"],
  });

  const imgY = useTransform(scrollYProgress, [0, 1], [60, -60]);
  const imgRotate = useTransform(scrollYProgress, [0, 1], [-3, 3]);

  return (
    <section
      ref={sectionRef}
      id="about"
      name="about"
      className="relative w-full px-6 md:px-12 lg:px-24 pt-16 md:pt-20 pb-20 md:pb-28 overflow-hidden"
    >
      <div className="absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full bg-aurora-teal/10 blur-[120px] pointer-events-none" />
      <div className="absolute -bottom-40 -right-40 w-[500px] h-[500px] rounded-full bg-aurora-magenta/10 blur-[120px] pointer-events-none" />

      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
        {/* Left: copy */}
        <motion.div
          initial={{ opacity: 0, x: -70 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        >
          <motion.span
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6 }}
            className="eyebrow"
          >
            <span className="dot" />
            About me
          </motion.span>

          <motion.h2
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.1 }}
            className="mt-5 font-jamjuree text-3xl md:text-5xl font-semibold text-white leading-tight"
          >
            Consultant and{" "}
            <span className="text-gradient-aurora">engineer</span>,
            <br />
            hands-on since 2022.
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
            className="mt-6 text-sm md:text-base text-white/70 leading-relaxed max-w-xl"
          >
            I'm Hamza, a software consultant based in Karachi. I lead a senior
            team that takes web, mobile and desktop products from architecture
            to launch, and I stay hands-on in the code on every project. You get
            honest estimates, clear timelines and engineering that holds up in
            production.
          </motion.p>

          <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-1 xl:grid-cols-3 gap-3 max-w-xl">
            {proofPoints.map((pt, i) => (
              <ProofPoint key={pt.label} {...pt} i={i} />
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="mt-10 grid grid-cols-3 gap-3 max-w-md"
          >
            {[
              { k: "Web", v: "React · Next" },
              { k: "Mobile", v: "Flutter · RN" },
              { k: "Desktop", v: ".NET · Electron" },
            ].map((c, i) => (
              <div
                key={i}
                className="glass rounded-xl px-3 py-3 text-center"
              >
                <div className="text-sm font-semibold text-white">{c.k}</div>
                <div className="text-[10px] text-white/55 uppercase tracking-wider mt-1">
                  {c.v}
                </div>
              </div>
            ))}
          </motion.div>
        </motion.div>

        {/* Right: image card with parallax */}
        <motion.div
          initial={{ opacity: 0, x: 80 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          className="relative flex justify-center lg:justify-end"
        >
          <motion.div
            style={{ y: imgY, rotate: imgRotate }}
            className="relative w-[280px] sm:w-[340px] md:w-[400px] aspect-[3/4]"
          >
            <div className="absolute -inset-2 bg-gradient-to-br from-aurora-teal via-aurora-magenta to-aurora-amber rounded-[36px] blur-2xl opacity-40" />
            <div className="relative w-full h-full rounded-[32px] overflow-hidden border border-white/10 shadow-card">
              <img
                src="/images/me1.jpg"
                alt="Hamza"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink-950/80 via-transparent to-transparent" />
              <div className="absolute bottom-4 left-4 right-4 glass-strong rounded-2xl px-4 py-3">
                <div className="text-[10px] text-white/55 uppercase tracking-[0.2em]">
                  Currently
                </div>
                <div className="text-sm text-white font-medium">
                  Lead Software Engineer at Vision Scale Labs
                </div>
              </div>
            </div>
          </motion.div>

          <div className="absolute -top-6 -right-6 w-24 h-24 rounded-2xl glass flex items-center justify-center rotate-6">
            <div className="text-center">
              <div className="text-2xl">🚀</div>
              <div className="text-[10px] text-white/60 uppercase tracking-wider mt-1">
                shipping
              </div>
            </div>
          </div>
          <div className="absolute -bottom-6 -left-2 w-28 h-20 rounded-2xl glass flex items-center justify-center -rotate-3">
            <div className="text-center">
              <div className="text-xs text-white/80 font-semibold">Now booking</div>
              <div className="text-[10px] text-white/55 uppercase tracking-wider mt-0.5">
                consultancy · long-term
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Section2;
